import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Alert, Spinner } from "react-bootstrap";
import * as Constants from "../api/constants";
import { Context } from "../App";
import { savePendingCheckout, clearPendingCheckout } from "../utils/razorpayReturn";
import "../partyBossTheme.css";

function loadRazorpayCheckoutScript() {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src*="checkout.razorpay.com"]')) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Razorpay script failed to load"));
    document.body.appendChild(script);
  });
}

export default function ResumePaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setContext } = useContext(Context);
  const ref = searchParams.get("ref") || "";
  const [message, setMessage] = useState("Preparing your payment…");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const pendingRef = useRef(null);
  const paymentCompletedRef = useRef(false);

  const updatePaymentDetails = useCallback(
    (rpResponse, csDetails) => {
      const isSuccess = Boolean(
        rpResponse?.razorpay_order_id && rpResponse?.razorpay_payment_id
      );
      let paystatus;
      let failReason = "";
      let orderId = "";
      let paymentId = "";
      let signature = "";
      if (isSuccess) {
        orderId = rpResponse.razorpay_order_id;
        paymentId = rpResponse.razorpay_payment_id;
        signature = rpResponse.razorpay_signature || "";
        paystatus = "SUCCESS";
      } else {
        failReason =
          rpResponse.error?.reason || rpResponse.error?.description || "";
        orderId = rpResponse.error?.metadata?.order_id || "";
        paymentId = rpResponse.error?.metadata?.payment_id || "";
        paystatus = "FAILED";
      }
      const reqObj = {
        id: csDetails?.eventId ?? csDetails?.id,
        paymentResponse: {
          status: paystatus,
          orderId,
          paymentId,
          signature,
          failedReason: failReason,
          amountReceived: csDetails?.payDetails?.amount,
        },
      };
      return axios.post(Constants.UPDATE_PD, reqObj).then((response) => {
        const bookingReference = response?.data?.bookingReference;
        if (paystatus === "FAILED") {
          setError(failReason || "Payment failed. Please try again from the email link.");
          setBusy(false);
          return;
        }
        clearPendingCheckout();
        setContext((prev) => ({
          ...(prev && typeof prev === "object" ? prev : {}),
          bookingReferenceId: bookingReference ?? prev?.bookingReferenceId,
        }));
        navigate("/event-details");
      }).catch(() => {
        setError("Could not record payment. Contact support if amount was debited.");
        setBusy(false);
      });
    },
    [navigate, setContext]
  );

  const openRazorpay = useCallback(
    async (csResponse) => {
      pendingRef.current = csResponse.data;
      paymentCompletedRef.current = false;
      savePendingCheckout({
        eventId: csResponse.data?.eventId,
        bookingReferenceId: csResponse.data?.bookingReference,
      });
      const payDetails = csResponse.data.payDetails;
      await loadRazorpayCheckoutScript();
      if (typeof window.Razorpay !== "function") {
        throw new Error("Razorpay could not load.");
      }
      const amount =
        typeof payDetails.amount === "number" ? payDetails.amount : Number(payDetails.amount);
      const options = {
        key: payDetails.keyId,
        amount,
        currency: payDetails.currency || "INR",
        name: payDetails.businessName,
        description: payDetails.description,
        image: payDetails.logoUrl || undefined,
        order_id: payDetails.orderId,
        prefill: {
          name: payDetails.custName,
          email: payDetails.custEmail,
          contact: payDetails.custPhone,
        },
        handler(response) {
          paymentCompletedRef.current = true;
          setBusy(true);
          updatePaymentDetails(response, csResponse.data);
        },
        modal: {
          escape: false,
          ondismiss() {
            if (paymentCompletedRef.current) return;
            setMessage("Payment window closed.");
            setBusy(false);
          },
        },
        theme: { color: "#3399cc" },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (ev) => {
        const cs = pendingRef.current;
        updatePaymentDetails(
          {
            razorpay_order_id: "",
            error: ev?.error || { reason: "PAYMENT_FAILED", description: "Payment failed", metadata: {} },
          },
          cs
        );
      });
      setMessage("Complete payment in the Razorpay window.");
      setBusy(false);
      rzp.open();
    },
    [updatePaymentDetails]
  );

  useEffect(() => {
    if (!ref) {
      setError("Missing booking reference.");
      setBusy(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.post(Constants.RESUME_BOOKING_PAYMENT, { bookingRefId: ref });
        if (cancelled) return;
        if (!response?.data?.payDetails?.orderId) {
          setError("Unable to start payment. The slot may no longer be available.");
          setBusy(false);
          return;
        }
        await openRazorpay(response);
      } catch (e) {
        if (cancelled) return;
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.status ||
          e?.message ||
          "Could not resume payment.";
        setError(msg);
        setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ref, openRazorpay]);

  return (
    <div className="party-boss-page w-full bg-[#05020a] pb-20 pt-20 text-white md:pt-24">
      <div className="px-[5%] lg:px-[8%]">
        <div className="mx-auto max-w-lg rounded-[28px] border border-white/15 bg-white/[0.06] p-8 text-center">
          <h1 className="mb-4 text-2xl font-black text-[#ffd166]">Continue payment</h1>
          {busy ? (
            <div>
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-white/80">{message}</span>
            </div>
          ) : null}
          {error ? <Alert variant="danger" className="mt-3 text-start">{error}</Alert> : null}
          {!busy && !error ? <p className="text-white/70 mb-0">{message}</p> : null}
        </div>
      </div>
    </div>
  );
}
